/**
 * tdesign v0.3.1
 * (c) 2022 tdesign
 * @license MIT
 */

import { _ as _defineProperty } from '../_chunks/dep-c445dc73.js';
import { _ as _slicedToArray } from '../_chunks/dep-fc32874d.js';
import React__default, { useRef, useMemo, useState } from 'react';
import { useUnmountedRef } from 'ahooks';
import { u as useSpring, a as animated } from '../_chunks/dep-d5ec015e.js';
import withNativeProps from '../_util/withNativeProps.js';
import { withStopPropagation } from '../_util/withStopPropagation.js';
import { renderToContainer } from '../_util/renderToContainer.js';
import { useLockScroll } from '../_util/useLockScroll.js';
import { useShouldRender } from '../_util/useShouldRender.js';
import useConfig from '../_util/useConfig.js';
import '../_chunks/dep-5cf1fbb1.js';
import '../_chunks/dep-f771ed13.js';
import '../_chunks/dep-b3e4e8ba.js';
import '../_chunks/dep-b31bb48b.js';
import '../_chunks/dep-ba137374.js';
import '../_chunks/dep-6b86f47a.js';
import '../_chunks/dep-9fe8d051.js';
import 'react-dom';
import 'classnames';
import '../_chunks/dep-dadffb76.js';
import '../_chunks/dep-674c58f4.js';
import '../_chunks/dep-95834911.js';
import '../_chunks/dep-0e9724f2.js';
import '../_chunks/dep-cd7df551.js';
import '../_chunks/dep-41e2f4ed.js';
import '../_chunks/dep-818f0859.js';
import '../_chunks/dep-a4df350d.js';
import '../_chunks/dep-00b2fb56.js';
import '../_chunks/dep-6b8f1e75.js';
import '../_chunks/dep-aea8138c.js';
import '../_chunks/dep-d8b4285c.js';
import '../_util/getContainer.js';
import '../_chunks/dep-3fecfff7.js';
import '../_util/canUseDom.js';
import '../_chunks/dep-c9dcffa1.js';
import '../_util/useTouch.js';
import '../_util/getScrollParent.js';
import '../_util/supportsPassive.js';
import '../_util/useInitialized.js';
import '../config-provider/ConfigContext.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var opacityRecord = {
  "default": 0.55,
  thin: 0.35,
  thick: 0.75
};
var defaultProps = {
  visible: true,
  destroyOnClose: false,
  forceRender: false,
  color: "black",
  opacity: "default",
  disableBodyScroll: true,
  getContainer: null,
  stopPropagation: ["click"]
};
var Overlay = function Overlay(props) {
  var ref = useRef(null);
  useLockScroll(ref, props.visible && props.disableBodyScroll);
  var _useConfig = useConfig(),
    classPrefix = _useConfig.classPrefix;
  var name = "".concat(classPrefix, "-overlay");
  var background = useMemo(function () {
    var _opacityRecord$props$;
    var opacity2 = (_opacityRecord$props$ = opacityRecord[props.opacity]) !== null && _opacityRecord$props$ !== void 0 ? _opacityRecord$props$ : props.opacity;
    var rgb = props.color === "white" ? "255, 255, 255" : "0, 0, 0";
    return "rgba(".concat(rgb, ", ").concat(opacity2, ")");
  }, [props.color, props.opacity]);
  var _useState = useState(props.visible),
    _useState2 = _slicedToArray(_useState, 2),
    active = _useState2[0],
    setActive = _useState2[1];
  var unmountedRef = useUnmountedRef();
  var _useSpring = useSpring({
      opacity: props.visible ? 1 : 0,
      config: {
        precision: 0.01,
        mass: 1,
        tension: 200,
        friction: 30,
        clamp: true
      },
      onStart: function onStart() {
        setActive(true);
      },
      onRest: function onRest() {
        if (unmountedRef.current) return;
        setActive(props.visible);
        if (props.visible) {
          var _props$afterShow;
          (_props$afterShow = props.afterShow) === null || _props$afterShow === void 0 ? void 0 : _props$afterShow.call(props);
        } else {
          var _props$afterClose;
          (_props$afterClose = props.afterClose) === null || _props$afterClose === void 0 ? void 0 : _props$afterClose.call(props);
        }
      }
    }),
    opacity = _useSpring.opacity;
  var shouldRender = useShouldRender(active, props.forceRender, props.destroyOnClose);
  var node = withStopPropagation(props.stopPropagation, withNativeProps(props, /* @__PURE__ */React__default.createElement(animated.div, {
    className: name,
    ref: ref,
    style: _objectSpread(_objectSpread({
      background: background,
      opacity: opacity
    }, props.style), {}, {
      display: active ? "unset" : "none"
    }),
    onClick: function onClick(e) {
      if (e.target === e.currentTarget) {
        var _props$onOverlayClick;
        (_props$onOverlayClick = props.onOverlayClick) === null || _props$onOverlayClick === void 0 ? void 0 : _props$onOverlayClick.call(props, e);
      }
    }
  }, props.onOverlayClick && /* @__PURE__ */React__default.createElement("div", {
    className: "".concat(classPrefix, "-aria-button"),
    role: "button",
    onClick: props.onOverlayClick
  }), /* @__PURE__ */React__default.createElement("div", {
    className: "".concat(classPrefix, "-content")
  }, shouldRender && props.children))));
  return renderToContainer(props.getContainer, node);
};
Overlay.defaultProps = defaultProps;
Overlay.displayName = "Overlay";

export { Overlay as default };
//# sourceMappingURL=Overlay.js.map
